import {
  Lead,
  JobberRequest,
  MatchDetails,
  UTMParams,
  ConfidenceLevel,
  ChannelGroup,
  MATCH_SCORES,
  CONFIDENCE_THRESHOLDS,
  CHANNEL_GROUPS
} from '@/types'

/**
 * Lead Matching Algorithm
 * Based on leads-dashboard-demo analysis
 * 
 * Scoring system:
 * - Email match: 40 points (highest confidence)
 * - Phone match: 30 points (high confidence)
 * - Name match: 20 points (medium confidence)
 * - Address match: 10 points (supporting evidence)
 * 
 * Confidence levels:
 * - High: 70%+ (likely same person)
 * - Medium: 40-69% (possible match, needs review)
 * - Low: 1-39% (weak match, manual verification needed)
 */

interface MatchResult {
  score: number
  details: MatchDetails
}

/**
 * Normalize phone number to digits only for comparison
 */
export function normalizePhone(phone?: string): string {
  if (!phone) return ''
  return phone.replace(/\D/g, '')
}

/**
 * Normalize email to lowercase for comparison
 */
export function normalizeEmail(email?: string): string {
  if (!email) return ''
  return email.toLowerCase().trim()
}

/**
 * Normalize name for comparison (remove spaces, punctuation, lowercase)
 */
export function normalizeName(name?: string): string {
  if (!name) return ''
  return name
    .toLowerCase()
    .replace(/[^a-z\u00C0-\u017F]/gi, '') // Remove non-letters, keep accented chars
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
}

/**
 * Normalize address for comparison
 */
export function normalizeAddress(address?: string): string {
  if (!address) return ''
  return address
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric
}

/**
 * Calculate match score between a lead and a Jobber request
 */
export function calculateMatchScore(lead: Lead, request: JobberRequest): MatchResult {
  const details: MatchDetails = {
    emailMatch: false,
    phoneMatch: false,
    nameMatch: false,
    addressMatch: false,
    emailScore: 0,
    phoneScore: 0,
    nameScore: 0,
    addressScore: 0
  }

  let totalScore = 0

  // Email match (40 points)
  if (lead.client_email && request.client.email) {
    const leadEmail = normalizeEmail(lead.client_email)
    const requestEmail = normalizeEmail(request.client.email)
    
    if (leadEmail === requestEmail && leadEmail !== '') {
      details.emailMatch = true
      details.emailScore = MATCH_SCORES.EMAIL_MATCH
      totalScore += MATCH_SCORES.EMAIL_MATCH
    }
  }

  // Phone match (30 points)
  if (lead.client_phone && request.client.phone) {
    const leadPhone = normalizePhone(lead.client_phone)
    const requestPhone = normalizePhone(request.client.phone)
    
    if (leadPhone === requestPhone && leadPhone !== '') {
      details.phoneMatch = true
      details.phoneScore = MATCH_SCORES.PHONE_MATCH
      totalScore += MATCH_SCORES.PHONE_MATCH
    }
  }

  // Name match (20 points)
  if (lead.client_name && request.client.name) {
    const leadName = normalizeName(lead.client_name)
    const requestName = normalizeName(request.client.name)
    
    if (leadName === requestName && leadName !== '') {
      details.nameMatch = true
      details.nameScore = MATCH_SCORES.NAME_MATCH
      totalScore += MATCH_SCORES.NAME_MATCH
    }
  }

  // Address match (10 points)
  if (lead.street_address && request.client.address.street) {
    const leadAddress = normalizeAddress(lead.street_address)
    const requestAddress = normalizeAddress(
      `${request.client.address.street}, ${request.client.address.city} ${request.client.address.province}`
    )
    
    if (leadAddress === requestAddress && leadAddress !== '') {
      details.addressMatch = true
      details.addressScore = MATCH_SCORES.ADDRESS_MATCH
      totalScore += MATCH_SCORES.ADDRESS_MATCH
    }
  }

  return {
    score: totalScore,
    details
  }
}

/**
 * Get confidence level based on match score
 */
export function getConfidenceLevel(score: number): ConfidenceLevel {
  if (score >= CONFIDENCE_THRESHOLDS.HIGH) return 'high'
  if (score >= CONFIDENCE_THRESHOLDS.MEDIUM) return 'medium'
  if (score >= CONFIDENCE_THRESHOLDS.LOW) return 'low'
  return 'none'
}

/**
 * Extract UTM parameters from a URL
 */
export function extractUTMParams(url?: string): UTMParams {
  if (!url) return {}
  
  try {
    const urlObj = new URL(url)
    const params: UTMParams = {}
    
    const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id']
    
    utmParams.forEach(param => {
      const value = urlObj.searchParams.get(param)
      if (value) {
        params[param as keyof UTMParams] = value
      }
    })
    
    return params
  } catch (error) {
    // Invalid URL, return empty params
    return {}
  }
}

/**
 * Categorize marketing channel based on UTM parameters
 */
export function categorizeChannel(utmParams: UTMParams): ChannelGroup {
  const { utm_source, utm_medium } = utmParams
  
  // Google Ads
  if (utm_source === 'google' && (utm_medium === 'cpc' || utm_medium === 'ppc')) {
    return 'Google Ads'
  }
  
  // Facebook Ads
  if (utm_source === 'facebook' && (utm_medium === 'cpc' || utm_medium === 'social' || utm_medium === 'ppc')) {
    return 'Facebook Ads'
  }
  
  // Bing Ads
  if (utm_source === 'bing' && (utm_medium === 'cpc' || utm_medium === 'ppc')) {
    return 'Bing Ads'
  }
  
  // Organic Search
  if ((utm_source === 'google' || utm_source === 'bing' || utm_source === 'yahoo') && utm_medium === 'organic') {
    return 'Organic Search'
  }
  
  // Email Marketing
  if (utm_medium === 'email') {
    return 'Email Marketing'
  }
  
  // Social Media (non-paid)
  if (utm_medium === 'social' && utm_source !== 'facebook') {
    return 'Social Media'
  }
  
  // Referral
  if (utm_medium === 'referral') {
    return 'Referral'
  }
  
  // Direct traffic
  if (!utm_source || utm_source === '(direct)' || (!utm_source && !utm_medium)) {
    return 'Direct'
  }
  
  // Any other source without medium (likely referral)
  if (utm_source && !utm_medium) {
    return 'Referral'
  }
  
  // Default to Unknown for unrecognized patterns
  return 'Unknown'
}

/**
 * Extract postal code from address string
 */
export function extractPostalCode(address: string): string | null {
  if (!address) return null
  
  // Canadian postal code pattern: A1A 1A1 or A1A1A1
  const postalCodePattern = /[A-Z]\d[A-Z]\s?\d[A-Z]\d/i
  const match = address.match(postalCodePattern)
  
  return match ? match[0].replace(/\s/g, '').toUpperCase() : null
}

/**
 * Get Forward Sortation Area (FSA) from postal code
 */
export function getPostalFSA(postalCode: string): string | null {
  if (!postalCode || postalCode.length < 3) return null
  return postalCode.substring(0, 3).toUpperCase()
}

/**
 * Find best matches for a lead against multiple Jobber requests
 */
export function findBestMatches(
  lead: Lead,
  requests: JobberRequest[],
  minScore: number = CONFIDENCE_THRESHOLDS.LOW
): Array<{ request: JobberRequest; match: MatchResult }> {
  const matches = requests
    .map(request => ({
      request,
      match: calculateMatchScore(lead, request)
    }))
    .filter(({ match }) => match.score >= minScore)
    .sort((a, b) => b.match.score - a.match.score)
  
  return matches
}

/**
 * Batch process leads and requests to find all matches
 */
export function batchMatchLeads(
  leads: Lead[],
  requests: JobberRequest[],
  minScore: number = CONFIDENCE_THRESHOLDS.LOW
): Array<{
  lead: Lead
  matches: Array<{ request: JobberRequest; match: MatchResult }>
}> {
  return leads.map(lead => ({
    lead,
    matches: findBestMatches(lead, requests, minScore)
  }))
}